import parse from '@lib/parse';
import { baseFilter } from '@packages/functions/lib/baseFilter';
import { TabObject } from '@packages/tabs/lib/TabContainer';
import { capitalize, find, startCase } from 'lodash';
import { Fragment } from 'react';

type TabTuple = [string, string, any, any];

/**
 * To string getter
 * @param tab
 * @param prop
 * @returns {*}
 * @private
 */
const _tabGetter = (tab: TabObject, prop = 'name') => {
    // $FlowFixMe
    let value = tab[prop];

    if (prop === 'name') {
        value = mapTabName(value);
    }

    return value;
};

/**
 * Functional tab creation
 * @example
 * [createTab('test-page')] // [{ name: 'test-page', label: 'Test Page' }]
 * [createTab('my-page', 'Awesome things!')] // [{ name: 'my-page', label: 'Awesome things!' }]
 *
 * @param name
 * @param label
 * @param component
 * @param validation
 * @returns {{name: string, label: string}}
 */
export const createTab = (name: string, label?: string, component?: any, validation?: any): TabObject => {
    component = component || Fragment;

    const tab: TabObject = {
        name: mapTabName(name),
        label: `${label ? label : capitalize(startCase(name))}`.trim(),
        component,
        validation,
    };

    tab.toString = (prop?: string): string => _tabGetter(tab, prop);

    return tab;
};

/**
 * Create tabs from string[], string[][] or TabObject[] values
 * @param tabs
 * @returns {*}
 */
export const createTabs = <T = TabTuple>(tabs: T[]): TabObject[] => {
    const newTabs = (tabs as T[]).filter(baseFilter).map((tab: T): TabObject | null => {
        if (tab) {
            if (typeof tab === 'string') {
                return createTab(tab);
            }

            if (typeof tab === 'object') {
                if (Array.isArray(tab) && tab.length > 0) {
                    const [name, ...rest] = tab;
                    return createTab(name, ...rest);
                } else {
                    // @ts-ignore
                    tab.toString = (prop) => _tabGetter(tab as any, prop);
                    return tab as any;
                }
            }
        }

        return tab as any;
    });

    return mapTabs((newTabs as any[]).filter(baseFilter));
};

/**
 * Tabs map
 * @param tabs
 * @returns {*}
 */
export const mapTabs = (tabs: string[] | TabObject[]) =>
    (tabs as []).map((tab: string | TabObject, index) =>
        typeof tab === 'string' ? { index, ...createTab(tab) } : { index, ...tab },
    );

/**
 * Trim and lowercase given name
 * @param name
 * @returns {string}
 */
const mapTabName = (name: string) => `${name}`.trim().toLowerCase();

/**
 * Search for given tab
 * @param tabs
 * @param tab
 * @param fallback
 * @returns {null|TabObject|*}
 */
export const searchTabs = (
    tabs: TabObject[],
    tab: string | number | Partial<TabObject> | ((tabs: TabObject[]) => TabObject),
    fallback = false,
): TabObject | null => {
    tabs = mapTabs(tabs);
    tab = parse(tab);

    if (tab === null || tab === '') {
        return tabs[0];
    }

    switch (typeof tab) {
        case 'undefined':
            return tabs[0];
        case 'number':
            return searchTabs(tabs, { index: tab });
        case 'string':
            return searchTabs(tabs, { name: tab, label: tab });
        case 'function':
            return searchTabs(tabs, tab(tabs));
        default:
            const tab$$ = tab as TabObject;
            const tabObject = find(tabs, (tab$) => {
                if (tab$ === tab$$) {
                    return true;
                }

                if (typeof tab$$.index !== 'undefined' && parseInt(`${tab$$.index}`) === parseInt(`${tab$.index}`)) {
                    return true;
                }

                if (typeof tab$$.id !== 'undefined' && mapTabName(`${tab$$.id}`) === mapTabName(`${tab$.id}`)) {
                    return true;
                }

                if (tab$$.name && mapTabName(tab$$.name) === mapTabName(tab$.name)) {
                    return true;
                }

                // noinspection RedundantIfStatementJS
                if (tab$$.label) {
                    if (mapTabName(tab$$.label) === mapTabName(tab$.label)) {
                        return true;
                    }
                }

                return false;
            });

            return tabObject ? tabObject : fallback ? tabs[0] : null;
    }
};
